import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { PageHeader } from "../components/ui/kaizen";
import CloudflareStreamCard from "../components/livestream/CloudflareStreamCard";
import ObsTestGuide from "../components/livestream/ObsTestGuide";
import BannerAndLinkCard from "../components/livestream/BannerAndLinkCard";

const VIEWER_STATUS_OPTIONS = [
  { v: "scheduled", l: "📅 Programmé", color: "border-blue-500/40 text-blue-300" },
  { v: "countdown", l: "⏳ Compte à rebours", color: "border-blue-500/40 text-blue-300" },
  { v: "live", l: "🔴 EN DIRECT", color: "border-red-500/50 text-red-300" },
  { v: "ended", l: "🏁 Terminé", color: "border-zinc-700 text-zinc-400" },
  { v: "replay_live", l: "▶ Replay actif", color: "border-emerald-500/40 text-emerald-300" },
];

const SOURCE_TYPES = [
  { v: "youtube_embed", l: "YouTube embed" },
  { v: "youtube_channel_live", l: "YouTube channel live" },
  { v: "playlist_embed", l: "YouTube playlist embed" },
  { v: "vimeo_embed", l: "Vimeo embed" },
  { v: "custom_embed", l: "Custom embed" },
  { v: "external_player", l: "External player" },
  { v: "cloudflare_stream", l: "Cloudflare Stream (lecture signée)" },
];

const ACCESS_TYPE_LABELS = {
  paid: "✅ Payé",
  sponsor: "🏆 Sponsor",
  media: "📰 Média",
  staff: "⚙️ Staff",
  complimentary: "🎁 Complimentaire",
};

export default function LivestreamModule() {
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [accessRights, setAccessRights] = useState([]);
  const [sessions, setSessions] = useState(null);

  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [eventForm, setEventForm] = useState({
    title_fr: "", title_en: "",
    description_fr: "", description_en: "",
    start_at: "", end_at: "",
    is_published: false,
    replay_enabled: true,
    viewer_status: "scheduled",
    max_concurrent_devices: 1,
  });

  const [productOpen, setProductOpen] = useState(false);
  const [productForm, setProductForm] = useState({
    name_fr: "Accès livestream + Replay 48h",
    name_en: "Livestream Access + 48h Replay",
    description_fr: "",
    description_en: "",
    price_cad: 15,
    replay_included: true,
    replay_duration_hours: 48,
    sort_order: 1,
  });

  const [sourceOpen, setSourceOpen] = useState(false);
  const [sourceForm, setSourceForm] = useState({
    source_type: "youtube_embed",
    source_url: "",
    backup_source_url: "",
    is_primary: true,
    notes: "",
    label: "",
  });

  const [grantOpen, setGrantOpen] = useState(false);
  const [grantForm, setGrantForm] = useState({
    customer_email: "", customer_name: "", access_type: "sponsor", reason: "",
  });

  const loadEvents = async () => {
    try {
      const r = await api.get("/livestream/events");
      setEvents(r.data);
      if (r.data.length && !selectedId) setSelectedId(r.data[0].id);
    } catch { toast.error("Erreur chargement livestreams"); }
  };

  const loadDetail = async (eid) => {
    if (!eid) return;
    try {
      const [d, dash, ar, sess] = await Promise.all([
        api.get(`/livestream/events/${eid}`),
        api.get(`/livestream/events/${eid}/dashboard`),
        api.get(`/livestream/events/${eid}/access-rights`),
        api.get(`/livestream/events/${eid}/sessions`).catch(() => ({ data: null })),
      ]);
      setDetail(d.data);
      setDashboard(dash.data);
      setAccessRights(ar.data);
      setSessions(sess.data);
    } catch { toast.error("Erreur détail livestream"); }
  };

  useEffect(() => { loadEvents(); }, []);
  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId]);

  const submitEvent = async () => {
    if (!eventForm.title_fr || !eventForm.title_en || !eventForm.start_at) {
      toast.error("Titre FR/EN + date début obligatoires");
      return;
    }
    try {
      // Convert datetime-local to ISO
      const payload = { ...eventForm };
      if (payload.start_at) payload.start_at = new Date(payload.start_at).toISOString();
      if (payload.end_at) payload.end_at = new Date(payload.end_at).toISOString();
      const r = await api.post("/livestream/events", payload);
      toast.success(`✅ ${r.data.title_fr} créé`);
      setCreateEventOpen(false);
      setEventForm({ ...eventForm, title_fr: "", title_en: "", description_fr: "", description_en: "", start_at: "", end_at: "" });
      await loadEvents();
      setSelectedId(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const changeViewerStatus = async (newStatus) => {
    try {
      await api.patch(`/livestream/events/${selectedId}`, { viewer_status: newStatus });
      toast.success(`✅ État viewer : ${VIEWER_STATUS_OPTIONS.find(o => o.v === newStatus)?.l}`);
      await loadDetail(selectedId);
      await loadEvents();
    } catch (e) { toast.error("Erreur"); }
  };

  const togglePublish = async () => {
    try {
      await api.patch(`/livestream/events/${selectedId}`, { is_published: !detail.event.is_published });
      await loadDetail(selectedId);
      await loadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const changeReplayHours = async (v) => {
    try {
      await api.patch(`/livestream/events/${selectedId}`, { replay_hours: parseInt(v) });
      toast.success(`Replay : ${v} h après la fin`);
      await loadDetail(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const submitProduct = async () => {
    try {
      await api.post(`/livestream/events/${selectedId}/products`, productForm);
      toast.success("✅ Produit ajouté");
      setProductOpen(false);
      await loadDetail(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteProduct = async (pid, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    try {
      await api.delete(`/livestream/products/${pid}`);
      toast.success("Supprimé");
      await loadDetail(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const submitSource = async () => {
    if (!sourceForm.source_url) { toast.error("URL obligatoire"); return; }
    try {
      await api.post(`/livestream/events/${selectedId}/sources`, sourceForm);
      toast.success("✅ Source vidéo ajoutée");
      setSourceOpen(false);
      setSourceForm({ ...sourceForm, source_url: "", backup_source_url: "", notes: "", label: "" });
      await loadDetail(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const updateSourceUrl = async (sid, newUrl) => {
    try {
      await api.patch(`/livestream/sources/${sid}`, { source_url: newUrl });
      toast.success("✅ URL source mise à jour");
      await loadDetail(selectedId);
    } catch { toast.error("Erreur"); }
  };

  const deleteSource = async (sid) => {
    if (!window.confirm("Supprimer cette source ?")) return;
    try {
      await api.delete(`/livestream/sources/${sid}`);
      toast.success("Supprimé");
      await loadDetail(selectedId);
    } catch { toast.error("Erreur"); }
  };

  const submitGrant = async () => {
    if (!grantForm.customer_email || !grantForm.customer_name) {
      toast.error("Email + nom obligatoires");
      return;
    }
    try {
      const r = await api.post(`/livestream/events/${selectedId}/grant`, grantForm);
      toast.success(`✅ Accès accordé à ${grantForm.customer_name}`);
      // Show URL
      const url = `${window.location.origin}/livestream/portail/${r.data.access_token}`;
      navigator.clipboard.writeText(url);
      toast.success("🔗 Lien d'accès copié dans le presse-papier");
      setGrantOpen(false);
      setGrantForm({ customer_email: "", customer_name: "", access_type: "sponsor", reason: "" });
      await loadDetail(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const revokeAccess = async (aid, name) => {
    if (!window.confirm(`Révoquer l'accès de ${name} ?`)) return;
    try {
      await api.patch(`/livestream/access-rights/${aid}/revoke`);
      toast.success("Révoqué");
      await loadDetail(selectedId);
    } catch { toast.error("Erreur"); }
  };

  const deleteEvent = async () => {
    if (!window.confirm(`Supprimer "${detail.event.title_fr}" ?`)) return;
    try {
      await api.delete(`/livestream/events/${selectedId}`);
      toast.success("Supprimé");
      setSelectedId(null);
      await loadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const copyToClipboard = (text, label = "Copié") => {
    navigator.clipboard.writeText(text);
    toast.success(`📋 ${label}`);
  };

  return (
    <div className="space-y-6 p-6 md:p-8" data-testid="livestream-module">
      {/* Header */}
      <PageHeader
        title="Livestream payant"
        breadcrumbs={[{ label: "Compétition & événements" }, { label: "Livestream" }]}
        subtitle="Diffusion en direct + replay payants. Tu contrôles paiement, accès, replay. YouTube/Vimeo = source remplaçable sans casser les droits clients."
        actions={
          <Button onClick={() => setCreateEventOpen(true)} className="bg-red-500 hover:bg-red-400 text-white font-semibold" data-testid="create-stream-btn">
            + Nouveau livestream
          </Button>
        }
      />

      {/* Events + Detail */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* List */}
        <div className="space-y-2" data-testid="streams-list">
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Livestreams</div>
          {events.length === 0 ? (
            <div className="text-xs text-zinc-500 italic py-4">Aucun livestream. Crée le premier !</div>
          ) : events.map((e) => {
            const isSel = selectedId === e.id;
            const statusOpt = VIEWER_STATUS_OPTIONS.find(o => o.v === e.viewer_status);
            return (
              <button key={e.id} onClick={() => setSelectedId(e.id)} className={`w-full text-left border rounded-xl p-4 transition ${isSel ? "border-red-500/50 bg-red-500/5" : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-600"}`} data-testid={`stream-card-${e.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm text-zinc-100 truncate">{e.title_fr}</div>
                  {statusOpt && <Badge variant="outline" className={`text-[10px] ${statusOpt.color}`}>{statusOpt.l}</Badge>}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">📅 {new Date(e.start_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}</div>
                <div className="text-[10px] text-zinc-400 mt-2">
                  {e.access_granted} accès · {(e.revenue_cad || 0).toLocaleString()} $
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 space-y-4">
          {detail && dashboard ? (
            <>
              {/* Header event */}
              <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-xl font-bold">{detail.event.title_fr}</div>
                    <div className="text-xs text-zinc-400">{detail.event.title_en}</div>
                    <div className="text-xs text-zinc-500 mt-2">
                      📅 {new Date(detail.event.start_at).toLocaleString("fr-CA")} {detail.event.end_at && <>→ {new Date(detail.event.end_at).toLocaleString("fr-CA", { timeStyle: "short" })}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 border border-zinc-800 rounded-lg px-3 py-2">
                      <Switch checked={detail.event.is_published} onCheckedChange={togglePublish} data-testid="publish-toggle" />
                      <span className="text-xs">{detail.event.is_published ? "Publié" : "Brouillon"}</span>
                    </div>
                    <button onClick={deleteEvent} className="text-red-400 hover:text-red-300 text-xs border border-red-500/40 rounded-lg px-3 py-2" data-testid="delete-stream-btn">🗑️</button>
                  </div>
                </div>
                {detail.source_status && !detail.source_status.source_ok && (
                  <div className="mt-3 rounded-xl border-2 border-red-500 bg-red-500/15 px-4 py-3 text-sm text-red-100 flex items-start gap-3" data-testid="stream-no-source-alert">
                    <span className="text-xl">🚨</span>
                    <div>
                      <div className="font-bold">Aucune source vidéo configurée</div>
                      <div className="text-xs text-red-200/80 mt-0.5">La publication est bloquée tant qu'aucune source principale n'est ajoutée. Pense aussi à une <b>source de secours</b> (2e lien) — le viewer pourra basculer en 1 clic en cas de panne.</div>
                    </div>
                    <Button onClick={() => setSourceOpen(true)} size="sm" className="ml-auto bg-red-600 hover:bg-red-500 text-white text-xs shrink-0" data-testid="stream-alert-add-source">+ Ajouter une source</Button>
                  </div>
                )}
                {detail.source_status?.source_ok && !detail.source_status.has_backup && (
                  <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="stream-no-backup-warning">
                    ⚠️ Aucune <b>source de secours</b>. Ajoute un lien de secours (champ « Backup » de la source primaire ou 2e source) pour que les spectateurs puissent basculer si le flux principal tombe.
                  </div>
                )}
                {detail.event.is_published && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] tracking-[0.3em] text-amber-400">URL PUBLIQUE :</span>
                    <a href={`${window.location.origin}/livestream/${detail.event.slug}`} target="_blank" rel="noreferrer" className="text-xs text-amber-300 hover:text-amber-200 underline truncate max-w-md" data-testid="public-url">
                      /livestream/{detail.event.slug}
                    </a>
                    <button onClick={() => copyToClipboard(`${window.location.origin}/livestream/${detail.event.slug}`, "URL publique copiée")} className="text-[10px] text-zinc-400 hover:text-white border border-zinc-700 rounded-full px-2 py-0.5">📋</button>
                  </div>
                )}

                {/* Viewer status control - critical */}
                <div className="mt-4 border-t border-zinc-800 pt-3">
                  <div className="text-[10px] tracking-[0.3em] text-red-400 mb-2">🎛️ ÉTAT VIEWER (ce que voient les acheteurs)</div>
                  <Select value={detail.event.viewer_status} onValueChange={changeViewerStatus}>
                    <SelectTrigger className="bg-black border-zinc-800 max-w-xs" data-testid="viewer-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VIEWER_STATUS_OPTIONS.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-zinc-500 mt-2">
                    Bascule en <strong>EN DIRECT</strong> au début, <strong>Replay actif</strong> après, <strong>Terminé</strong> à la fin.
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="replay-hours-block">
                    <span className="text-[10px] tracking-[0.3em] text-emerald-400">⏱️ DURÉE DU REPLAY</span>
                    <Select value={String(detail.event.replay_hours || 48)} onValueChange={changeReplayHours}>
                      <SelectTrigger className="bg-black border-zinc-800 w-40" data-testid="replay-hours-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[24, 48, 72, 168].map((h) => <SelectItem key={h} value={String(h)}>{h === 168 ? "7 jours" : `${h} h`} après la fin</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {detail.replay_until && <span className="text-[11px] text-zinc-400" data-testid="replay-until">Replay accessible jusqu'au <b className="text-zinc-200">{new Date(detail.replay_until).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" })}</b></span>}
                    {!detail.event.replay_enabled && <span className="text-[11px] text-zinc-500">(replay désactivé)</span>}
                  </div>
                </div>
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-kpis">
                <KPI label="Revenus" value={`${(dashboard.kpis.revenue_cad || 0).toLocaleString()} $`} accent />
                <KPI label="Accès payés" value={dashboard.kpis.paid_access} />
                <KPI label="Accès accordés (manuel)" value={dashboard.kpis.manual_grants} />
                <KPI label="Source vidéo" value={dashboard.source_ok ? "✅ OK" : "⚠️ MANQUE"} accent={!dashboard.source_ok} />
              </div>

              {/* Video sources */}
              <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] tracking-[0.3em] text-amber-400">SOURCES VIDÉO (remplaçables sans casser l'accès)</div>
                  <Button onClick={() => setSourceOpen(true)} size="sm" className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 text-xs" data-testid="add-source-btn">+ Source</Button>
                </div>
                {detail.video_sources?.length === 0 ? (
                  <div className="text-xs text-zinc-500 italic py-3">⚠️ Aucune source vidéo. Ajoute le YouTube embed avant l'événement.</div>
                ) : (
                  <div className="space-y-2" data-testid="video-sources-list">
                    {detail.video_sources.map((s) => (
                      <div key={s.id} className={`border rounded-lg p-3 ${s.is_primary ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-black/30"}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold">{s.label ? `📹 ${s.label} · ` : ""}{SOURCE_TYPES.find(st => st.v === s.source_type)?.l || s.source_type}</span>
                              {s.is_primary && <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]">PRIMAIRE</Badge>}
                            </div>
                            <Input
                              defaultValue={s.source_url}
                              onBlur={(e) => { if (e.target.value !== s.source_url) updateSourceUrl(s.id, e.target.value); }}
                              className="bg-black border-zinc-800 text-xs mt-2 font-mono"
                              data-testid={`source-url-${s.id}`}
                            />
                            {s.last_verified_at && <div className="text-[10px] text-zinc-500 mt-1">Mis à jour : {new Date(s.last_verified_at).toLocaleString("fr-CA")}</div>}
                          </div>
                          <button onClick={() => deleteSource(s.id)} className="text-red-400 hover:text-red-300 text-xs">🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <CloudflareStreamCard eventId={selectedId} liveInputUid={detail.video_sources?.find((s) => s.source_type === "cloudflare_stream" && s.is_active)?.source_url} />
              <ObsTestGuide />
              <BannerAndLinkCard detail={detail} eventId={selectedId} reload={() => loadDetail(selectedId)} />

              {/* Products */}
              <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] tracking-[0.3em] text-amber-400">PRODUITS D'ACCÈS</div>
                  <Button onClick={() => setProductOpen(true)} size="sm" className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 text-xs" data-testid="add-product-btn">+ Produit</Button>
                </div>
                {detail.products?.length === 0 ? (
                  <div className="py-3 space-y-2"><div className="text-xs text-zinc-500 italic">Aucun produit.</div>
                    <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-black text-xs" data-testid="add-launch-product-btn" onClick={async () => { try { await api.post(`/livestream/events/${selectedId}/products`, { name_fr: "Accès familial — Direct + Replay 48 h", name_en: "Family access — Live + 48 h replay", description_fr: "Tarif de lancement : un accès pour regarder ensemble dans un même foyer. Direct de la programmation couverte par nos caméras et replay accessible 48 heures après la fin de l'événement. L'horaire peut évoluer selon le déroulement des rencontres.", description_en: "Launch price: one access to watch together in the same household. Live coverage of the scheduled program and replay available for 48 hours after the event ends.", price_cad: 10, replay_included: true, replay_duration_hours: 48, sort_order: 0 }); toast.success("Produit lancement 10 $ créé"); loadDetail(selectedId); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } }}>⚡ Créer le produit lancement 10 $ (direct + replay 48 h)</Button></div>
                ) : (
                  <div className="space-y-2" data-testid="products-admin-list">
                    {detail.products.map((p) => (
                      <div key={p.id} className="border border-zinc-800 rounded-lg p-3 bg-black/30 flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold">{p.name_fr}</div>
                          <div className="text-[10px] text-zinc-500">{p.name_en}</div>
                          <div className="text-xs mt-1">
                            <span className="font-bold text-amber-300">{p.price_cad.toFixed(2)} $</span>
                            {p.replay_included ? <span className="text-emerald-400"> · Replay {p.replay_duration_hours}h</span> : <span className="text-zinc-500"> · Direct seul</span>}
                          </div>
                        </div>
                        <button onClick={() => deleteProduct(p.id, p.name_fr)} className="text-red-400 hover:text-red-300 text-xs">🗑️</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Access Rights */}
              <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] tracking-[0.3em] text-amber-400">DROITS D'ACCÈS</div>
                  <Button onClick={() => setGrantOpen(true)} size="sm" className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-xs" data-testid="grant-access-btn">+ Accorder manuellement</Button>
                </div>
                {accessRights.length === 0 ? (
                  <div className="text-xs text-zinc-500 italic py-3">Aucun accès. Vente Stripe ou accord manuel sponsor/media/staff.</div>
                ) : (
                  <div className="space-y-2" data-testid="access-rights-list">
                    {accessRights.slice(0, 30).map((ar) => {
                      const url = `${window.location.origin}/livestream/portail/${ar.access_token}`;
                      return (
                        <div key={ar.id} className={`border rounded-lg p-3 ${ar.is_active ? "border-zinc-800 bg-black/30" : "border-zinc-900 bg-zinc-950/30 opacity-50"} flex items-start justify-between gap-2`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold">{ar.customer_name}</span>
                              <Badge variant="outline" className="text-[10px]">{ACCESS_TYPE_LABELS[ar.access_type] || ar.access_type}</Badge>
                              {!ar.is_active && <Badge variant="outline" className="border-red-500/40 text-red-300 text-[10px]">RÉVOQUÉ</Badge>}
                            </div>
                            <div className="text-[10px] text-zinc-500">{ar.customer_email}</div>
                            {ar.reason && <div className="text-[10px] text-zinc-400 italic mt-0.5">{ar.reason}</div>}
                            <div className="flex items-center gap-1 mt-2">
                              <button onClick={() => copyToClipboard(url, "Lien d'accès copié")} className="text-[10px] text-amber-300 hover:text-amber-200 border border-amber-500/30 rounded-full px-2 py-0.5">📋 Copier lien</button>
                              <a href={url} target="_blank" rel="noreferrer" className="text-[10px] text-zinc-400 hover:text-white border border-zinc-700 rounded-full px-2 py-0.5">👁️ Voir</a>
                            </div>
                          </div>
                          {ar.is_active && (
                            <button onClick={() => revokeAccess(ar.id, ar.customer_name)} className="text-red-400 hover:text-red-300 text-xs">Révoquer</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* V2 — Sessions & Anti-partage */}
              <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40" data-testid="sessions-panel">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="text-[10px] tracking-[0.3em] text-amber-400">🔒 SESSIONS & ANTI-PARTAGE</div>
                  {sessions && (
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="border border-emerald-500/40 text-emerald-300 rounded-full px-2 py-0.5">🟢 {sessions.live_now} en direct</span>
                      <span className="border border-zinc-700 text-zinc-300 rounded-full px-2 py-0.5">{sessions.unique_viewers} spectateur(s)</span>
                      <span className="border border-zinc-700 text-zinc-400 rounded-full px-2 py-0.5">Max {sessions.max_concurrent_devices} appareil(s)</span>
                      {sessions.suspicious_count > 0 && (
                        <span className="border border-red-500/50 text-red-300 rounded-full px-2 py-0.5">⚠️ {sessions.suspicious_count} partage(s) suspect(s)</span>
                      )}
                    </div>
                  )}
                </div>
                {!sessions || sessions.total_sessions === 0 ? (
                  <div className="text-xs text-zinc-500 italic py-3">Aucune session de visionnage encore. Les statistiques apparaîtront quand des spectateurs ouvriront leur portail.</div>
                ) : (
                  <div className="space-y-2" data-testid="sharing-report-list">
                    {sessions.sharing_report.map((s) => (
                      <div key={s.customer_email} className={`border rounded-lg p-3 flex items-start justify-between gap-2 ${s.suspicious ? "border-red-500/40 bg-red-500/5" : "border-zinc-800 bg-black/30"}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold">{s.customer_name}</span>
                            {s.suspicious && <Badge variant="outline" className="border-red-500/50 text-red-300 text-[10px]">⚠️ PARTAGE SUSPECT</Badge>}
                          </div>
                          <div className="text-[10px] text-zinc-500">{s.customer_email}</div>
                          <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-400">
                            <span>📱 {s.device_count} appareil(s)</span>
                            <span>🌐 {s.ip_count} IP(s)</span>
                            {s.blocked_attempts > 0 && <span className="text-red-300">🚫 {s.blocked_attempts} bloqué(s)</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="border border-zinc-800 rounded-2xl p-12 text-center bg-zinc-950/30">
              <div className="text-zinc-500 text-sm">Sélectionne un livestream (ou crée-en un)</div>
            </div>
          )}
        </div>
      </div>

      {/* Create event dialog */}
      <Dialog open={createEventOpen} onOpenChange={setCreateEventOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl" data-testid="create-stream-dialog">
          <DialogHeader><DialogTitle>Nouveau livestream</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Titre FR *</Label><Input value={eventForm.title_fr} onChange={(e) => setEventForm({ ...eventForm, title_fr: e.target.value })} placeholder="Gala KAIZEN 2026 — LIVESTREAM" className="bg-black border-zinc-800" data-testid="stream-title-fr" /></div>
              <div><Label className="text-xs">Titre EN *</Label><Input value={eventForm.title_en} onChange={(e) => setEventForm({ ...eventForm, title_en: e.target.value })} placeholder="KAIZEN Gala 2026 — LIVESTREAM" className="bg-black border-zinc-800" data-testid="stream-title-en" /></div>
            </div>
            <div><Label className="text-xs">Description FR</Label><Textarea value={eventForm.description_fr} onChange={(e) => setEventForm({ ...eventForm, description_fr: e.target.value })} className="bg-black border-zinc-800 min-h-[60px]" /></div>
            <div><Label className="text-xs">Description EN</Label><Textarea value={eventForm.description_en} onChange={(e) => setEventForm({ ...eventForm, description_en: e.target.value })} className="bg-black border-zinc-800 min-h-[60px]" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Début *</Label><Input type="datetime-local" value={eventForm.start_at} onChange={(e) => setEventForm({ ...eventForm, start_at: e.target.value })} className="bg-black border-zinc-800" data-testid="stream-start" /></div>
              <div><Label className="text-xs">Fin</Label><Input type="datetime-local" value={eventForm.end_at} onChange={(e) => setEventForm({ ...eventForm, end_at: e.target.value })} className="bg-black border-zinc-800" /></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 border border-zinc-800 rounded-lg px-3 py-2">
                <Switch checked={eventForm.is_published} onCheckedChange={(v) => setEventForm({ ...eventForm, is_published: v })} />
                <span className="text-xs">Publier</span>
              </div>
              <div className="flex items-center gap-2 border border-zinc-800 rounded-lg px-3 py-2">
                <Switch checked={eventForm.replay_enabled} onCheckedChange={(v) => setEventForm({ ...eventForm, replay_enabled: v })} />
                <span className="text-xs">Replay activé</span>
              </div>
            </div>
            <div className="border border-zinc-800 rounded-lg px-3 py-2">
              <Label className="text-xs flex items-center gap-1">🔒 Appareils simultanés max (anti-partage)</Label>
              <Input type="number" min={1} max={10} value={eventForm.max_concurrent_devices} onChange={(e) => setEventForm({ ...eventForm, max_concurrent_devices: Math.max(1, parseInt(e.target.value) || 1) })} className="bg-black border-zinc-800 mt-1 w-32" data-testid="stream-max-devices" />
              <p className="text-[10px] text-zinc-500 mt-1">1 = un seul appareil par lien d'accès (recommandé). Au-delà, l'accès est bloqué.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateEventOpen(false)}>Annuler</Button>
            <Button onClick={submitEvent} className="bg-red-500 hover:bg-red-400 text-white" data-testid="submit-stream">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product dialog */}
      <Dialog open={productOpen} onOpenChange={setProductOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-xl">
          <DialogHeader><DialogTitle>Nouveau produit d'accès</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Nom FR</Label><Input value={productForm.name_fr} onChange={(e) => setProductForm({ ...productForm, name_fr: e.target.value })} className="bg-black border-zinc-800" /></div>
              <div><Label className="text-xs">Nom EN</Label><Input value={productForm.name_en} onChange={(e) => setProductForm({ ...productForm, name_en: e.target.value })} className="bg-black border-zinc-800" /></div>
            </div>
            <div><Label className="text-xs">Description FR</Label><Textarea value={productForm.description_fr} onChange={(e) => setProductForm({ ...productForm, description_fr: e.target.value })} className="bg-black border-zinc-800 min-h-[50px]" /></div>
            <div><Label className="text-xs">Description EN</Label><Textarea value={productForm.description_en} onChange={(e) => setProductForm({ ...productForm, description_en: e.target.value })} className="bg-black border-zinc-800 min-h-[50px]" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Prix CAD</Label><Input type="number" value={productForm.price_cad} onChange={(e) => setProductForm({ ...productForm, price_cad: Number(e.target.value) })} className="bg-black border-zinc-800" /></div>
              <div className="flex items-center gap-2 border border-zinc-800 rounded-lg px-2"><Switch checked={productForm.replay_included} onCheckedChange={(v) => setProductForm({ ...productForm, replay_included: v })} /><span className="text-[10px]">Replay</span></div>
              <div><Label className="text-xs">Durée (h)</Label><Input type="number" value={productForm.replay_duration_hours} onChange={(e) => setProductForm({ ...productForm, replay_duration_hours: Number(e.target.value) })} className="bg-black border-zinc-800" disabled={!productForm.replay_included} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductOpen(false)}>Annuler</Button>
            <Button onClick={submitProduct} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="submit-product">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Source dialog */}
      <Dialog open={sourceOpen} onOpenChange={setSourceOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-xl">
          <DialogHeader><DialogTitle>Nouvelle source vidéo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={sourceForm.source_type} onValueChange={(v) => setSourceForm({ ...sourceForm, source_type: v })}>
                <SelectTrigger className="bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCE_TYPES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">URL source *</Label><Input value={sourceForm.source_url} onChange={(e) => setSourceForm({ ...sourceForm, source_url: e.target.value })} placeholder={sourceForm.source_type === "cloudflare_stream" ? "UID du live input Cloudflare (ex. 2343edecd535342819e348fc0d69fd7d)" : "https://www.youtube.com/embed/VIDEO_ID"} className="bg-black border-zinc-800 font-mono text-xs" data-testid="source-url-input" />{sourceForm.source_type === "cloudflare_stream" && <div className="text-[10px] text-orange-300 mt-1">Pas la Stream Key RTMPS : seulement l'UID (Live Input ID). La lecture signée est générée par le serveur.</div>}</div>
            <div><Label className="text-xs">URL backup (optionnel)</Label><Input value={sourceForm.backup_source_url} onChange={(e) => setSourceForm({ ...sourceForm, backup_source_url: e.target.value })} className="bg-black border-zinc-800 font-mono text-xs" /></div>
            <div className="flex items-center gap-2 border border-zinc-800 rounded-lg px-3 py-2">
              <Switch checked={sourceForm.is_primary} onCheckedChange={(v) => setSourceForm({ ...sourceForm, is_primary: v })} />
              <span className="text-xs">Source primaire (affichée aux viewers)</span>
            </div>
            <div><Label className="text-xs">Libellé caméra (multi-surfaces, ex. « Surface 1 »)</Label><Input value={sourceForm.label} onChange={(e) => setSourceForm({ ...sourceForm, label: e.target.value })} className="bg-black border-zinc-800 text-xs" placeholder="Surface 1" data-testid="source-label-input" /><div className="text-[10px] text-zinc-500 mt-1">Ajoute une source Cloudflare par surface (un live input chacune) : le spectateur bascule d'une caméra à l'autre dans le lecteur.</div></div>
            <div><Label className="text-xs">Notes</Label><Textarea value={sourceForm.notes} onChange={(e) => setSourceForm({ ...sourceForm, notes: e.target.value })} className="bg-black border-zinc-800 min-h-[50px]" placeholder="Stream principal à mettre à jour le jour de l'event..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSourceOpen(false)}>Annuler</Button>
            <Button onClick={submitSource} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="submit-source">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual grant dialog */}
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-xl">
          <DialogHeader><DialogTitle>Accorder un accès manuel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Nom *</Label><Input value={grantForm.customer_name} onChange={(e) => setGrantForm({ ...grantForm, customer_name: e.target.value })} className="bg-black border-zinc-800" data-testid="grant-name" /></div>
              <div><Label className="text-xs">Email *</Label><Input type="email" value={grantForm.customer_email} onChange={(e) => setGrantForm({ ...grantForm, customer_email: e.target.value })} className="bg-black border-zinc-800" data-testid="grant-email" /></div>
            </div>
            <div>
              <Label className="text-xs">Type d'accès</Label>
              <Select value={grantForm.access_type} onValueChange={(v) => setGrantForm({ ...grantForm, access_type: v })}>
                <SelectTrigger className="bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ACCESS_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Raison</Label><Textarea value={grantForm.reason} onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })} className="bg-black border-zinc-800 min-h-[50px]" placeholder="Niveau Platine — droit livestream + replay inclus" /></div>
            <div className="text-[10px] text-zinc-500 italic">Un lien d'accès personnel sera généré et copié dans ton presse-papier après création.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantOpen(false)}>Annuler</Button>
            <Button onClick={submitGrant} className="bg-emerald-500 hover:bg-emerald-400 text-black" data-testid="submit-grant">Accorder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-950/50"}`}>
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent ? "text-amber-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}
